import { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import DateRangeSelector from "./DateRangeSelector";
import ExportOptions from "./ExportOptions";
import ReportTable from "./ReportTable";
import { reportsApi } from "./reportsApi";

const AUDIT_DESCRIPTION =
  "Who signed in, and which records were created or deleted, including the user, action, and time.";

const filterSelectSx = {
  background: "#FFFFFF",
  borderRadius: "4px",
  height: "30px",
  fontFamily: "Inter, sans-serif",
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: "16px",
  "& fieldset": {
    borderColor: "#E5E7EB",
  },
};

const ACTION_OPTIONS = [
  { value: "ALL", label: "All actions" },
  { value: "LOGIN", label: "Login" },
  { value: "CREATE", label: "Create" },
  { value: "DELETE", label: "Delete" },
];

const AUDIT_DISPLAY_LABELS = {
  actorName: "Name",
  entityType: "Entity",
};

const relabelAuditColumns = (columns, translate) => {
  if (!Array.isArray(columns)) return columns;
  return columns.map((column) => {
    const labelKey = AUDIT_DISPLAY_LABELS[column?.key];
    return labelKey ? { ...column, label: translate(labelKey) } : column;
  });
};

const formatDateLabel = (dateOptions) => {
  if (dateOptions.duration === "custom") {
    const start = dateOptions.customDateRange?.startDate;
    const end = dateOptions.customDateRange?.endDate;
    return start && end ? `${start} to ${end}` : "Custom";
  }
  return dateOptions.duration;
};

const AuditLogReport = ({ users = [], t }) => {
  const [actorUserId, setActorUserId] = useState("");
  const [action, setAction] = useState("");
  const [dateOptions, setDateOptions] = useState({
    duration: "today",
    customDateRange: { startDate: "", endDate: "" },
  });
  const [exportFormat, setExportFormat] = useState("pdf");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const actorOptions = useMemo(
    () =>
      (Array.isArray(users) ? users : [])
        .map((user) => ({
          id: user?.id ?? user?.userId,
          name: user?.name || user?.userName || user?.email || "User",
          email: user?.email || "",
        }))
        .filter((user) => user.id),
    [users]
  );

  const selectedActor = actorOptions.find(
    (user) => String(user.id) === String(actorUserId)
  );
  const actionSelected = Boolean(action);
  const selectedAction =
    ACTION_OPTIONS.find((option) => option.value === action)?.label ||
    "Select an action";

  const handleGenerate = async () => {
    if (
      dateOptions.duration === "custom" &&
      (!dateOptions.customDateRange?.startDate ||
        !dateOptions.customDateRange?.endDate)
    ) {
      setError(t("Custom date range requires a start and end date"));
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await reportsApi.getAuditLog({
        actorUserId,
        action: action === "ALL" ? "" : action,
        dateOption: dateOptions.duration,
        customStartDate: dateOptions.customDateRange?.startDate,
        customEndDate: dateOptions.customDateRange?.endDate,
      });

      const rows = Array.isArray(response?.data) ? response.data : [];
      const nameFilterValue = selectedActor
        ? `${selectedActor.name}${selectedActor.email ? ` (${selectedActor.email})` : ""}`
        : t("All");
      setReportData({
        ...response,
        reportType: "audit",
        columns: relabelAuditColumns(response?.columns, t),
        data: rows,
        previewTitle: t("Audit Log"),
        previewHeaderDetails: [
          { label: t("Generated On"), value: new Date().toLocaleString() },
          { label: t("Total Records"), value: String(rows.length) },
        ],
        previewFilterDetails: [
          {
            label: t("Name"),
            value: nameFilterValue,
          },
          { label: t("Action"), value: t(selectedAction) },
          { label: t("Date Range"), value: formatDateLabel(dateOptions) },
        ],
        previewDetails: [
          {
            label: t("Name"),
            value: selectedActor ? selectedActor.name : t("All"),
          },
          { label: t("Action"), value: t(selectedAction) },
          { label: t("Date Range"), value: formatDateLabel(dateOptions) },
          { label: t("Total Records"), value: String(rows.length) },
        ],
      });
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message ||
        requestError?.response?.data?.error ||
        t("Unable to load the audit log");
      setError(message);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 360px" },
          gap: { xs: 3, lg: 4 },
          alignItems: "start",
          px: { xs: 0, lg: 2 },
        }}
      >
        <Box sx={{ pt: { xs: 0, lg: 1.5 } }}>
          <Typography
            sx={{
              color: "#1F2937",
              fontFamily: "Roboto, sans-serif",
              fontSize: "22px",
              fontWeight: 600,
              lineHeight: "24px",
              mb: 1.5,
            }}
          >
            {t("Audit Log")} {t("Report")}
          </Typography>
          <Typography
            sx={{
              color: "#6B7280",
              fontFamily: "Inter, sans-serif",
              fontSize: "18px",
              fontWeight: 400,
              lineHeight: "24px",
              maxWidth: 560,
            }}
          >
            {t(AUDIT_DESCRIPTION)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            columnGap: 1.5,
            rowGap: 1.5,
            "& .report-filter-label": {
              color: "#64748B",
              fontFamily: "Inter, sans-serif",
              fontSize: "10px",
              fontWeight: 400,
              lineHeight: "15px",
              mb: 0.5,
            },
          }}
        >
          <Box>
            <Typography className="report-filter-label">{t("Name")}</Typography>
            <FormControl sx={{ width: "100%" }}>
              <Select
                value={actorUserId}
                onChange={(event) => setActorUserId(event.target.value)}
                displayEmpty
                sx={filterSelectSx}
              >
                <MenuItem value="">{t("All")}</MenuItem>
                {actorOptions.map((user) => (
                  <MenuItem key={user.id} value={String(user.id)}>
                    {user.name}
                    {user.email ? ` (${user.email})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography className="report-filter-label">{t("Duration")}</Typography>
            <DateRangeSelector
              dateOptions={dateOptions}
              onChange={setDateOptions}
              sx={{ width: "100%" }}
              compact
            />
          </Box>

          <Box>
            <Typography className="report-filter-label">{t("Action")}</Typography>
            <FormControl sx={{ width: "100%" }}>
              <Select
                value={action}
                onChange={(event) => setAction(event.target.value)}
                displayEmpty
                renderValue={(selected) =>
                  selected
                    ? t(
                        ACTION_OPTIONS.find((option) => option.value === selected)
                          ?.label || "Select an action"
                      )
                    : t("Select an action")
                }
                sx={filterSelectSx}
              >
                {ACTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value || "all"} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={loading || !actionSelected}
            sx={{
              gridColumn: { xs: "1", sm: "1 / -1" },
              backgroundColor: "#183059",
              color: "#F6F6F7",
              "&:hover": { backgroundColor: "#102647" },
              "&.Mui-disabled": {
                backgroundColor: "#9CA3AF",
                color: "#F6F6F7",
              },
              height: "32px",
              textTransform: "none",
              borderRadius: "6px",
              fontFamily: "Inter, sans-serif",
              fontSize: "12px",
              fontWeight: 600,
              lineHeight: "16px",
              boxShadow: "none",
            }}
          >
            {loading ? <CircularProgress size={18} sx={{ color: "#F6F6F7" }} /> : t("Generate Report")}
          </Button>
        </Box>
      </Box>

      {error && (
        <Typography sx={{ color: "#B91C1C", fontSize: 13 }}>{error}</Typography>
      )}

      {reportData && (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <ExportOptions
              reportData={reportData}
              exportFormat={exportFormat}
              setExportFormat={setExportFormat}
            />
          </Box>
          <ReportTable reportData={reportData} />
        </>
      )}
    </Box>
  );
};

export default AuditLogReport;
